// F4: pure helpers behind the player transcript panel (kept JSX-free so vitest can cover them without a DOM).
import type { TranscriptOut, TranscriptSegmentOut } from "../api/types";

/**
 * React key for a transcript segment. Finalized transcripts (Orchard or an org/personal STT
 * account) carry a stable, server-minted UUID v7 `id` (migration 0022) that survives
 * re-diarization and re-transcription, so the key — and anything F1 cites by id — stays put
 * when speakers are relabelled. Live-only transcripts get no ids (Grackle decision
 * 2026-10-08), so those fall back to the segment's position.
 */
export function segmentKey(seg: Pick<TranscriptSegmentOut, "id">, index: number): string {
  return seg.id ? seg.id : `pos-${index}`;
}

/** DOM anchor (`#seg-<id>`) for citing a segment; none for id-less (live-only) segments. */
export function segmentAnchorId(seg: Pick<TranscriptSegmentOut, "id">): string | undefined {
  return seg.id ? `seg-${seg.id}` : undefined;
}

/**
 * Provenance line under the transcript (F4 §8): "Transcribed by Orchardrun (hosted)" for the
 * Grackle-hosted default, "Transcribed by <account label>" for an org account, and
 * "… (personal account)" when a member's own account ran it. Null when there's no provenance.
 */
export function transcriptProvenanceLine(
  t: { provenance?: TranscriptOut["provenance"]; provider_label?: string | null; model_id?: string | null },
): string | null {
  if (!t.provenance) return null;
  const who = t.provider_label || t.model_id;
  switch (t.provenance) {
    case "hosted":
      return `Transcribed by ${t.provider_label || "Orchardrun"} (hosted)`;
    case "organization":
      return who ? `Transcribed by ${who}` : "Transcribed by this organization's account";
    case "personal":
      return who ? `Transcribed by ${who} (personal account)` : "Transcribed by a personal account";
    default:
      return who ? `Transcribed by ${who}` : `Transcribed (${t.provenance})`;
  }
}
